import { Form, Modal, notification } from 'antd'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useStore } from 'store/useStore'
import { ElockInfoStatus } from 'sections/LKSection/modules/ELockModule/types'
import { PickPointFilesForm } from 'sections/LKSection/shared/PickPointSharedModule/components/PickPointFilesForm/PickPointFilesForm'
import { signboardFormatOptions, signboardSizeOptions } from 'sections/LKSection/shared/PickPointSharedModule/data'
import { Margin } from 'components/ui'
import { Info } from 'components/ui/Info/Info'
import { ReactSelect } from 'components/ui/Select/ReactSelect'
import { Button, Divider, Flex, FormItem, Text, TextArea, Title } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { LKRoutesEnum } from 'shared/constants/routes'
import { BaseOption, Country } from 'shared/types/types'

import { OrderKeys } from '../../../data'
import { OrderStatus, SignboardFormat, SignboardSize } from '../../../types'
import { getCreateOrderPageKey } from '../../../utils'
import { OrderBYCompanyForm } from './OrderBYCompanyForm/OrderBYCompanyForm'
import { validateOrderActivate } from './validate'

export const ActivateOrderForm = observer(() => {
  const { activateOrderUiStore: uiStore, viewOrderUiStore, orderStore, appStore } = useStore()

  const navigate = useNavigate()
  const { t } = useTranslation()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const order = viewOrderUiStore.selectedOrder
  const eLockStatus = order?.eLockInfo?.status
  const country = appStore.companyCountry

  const validateForm = () => {
    if (validateOrderActivate.validate(uiStore, appStore.companyCountry)) {
      setIsModalOpen(true)
    }
  }

  const activateOrderHandler = async () => {
    setLoading(true)

    const orderId = uiStore.orderId

    if (!orderId) {
      return
    }

    try {
      await uiStore.updateOrder(orderId, true)

      if (order?.status === OrderStatus.accepted) {
        await uiStore.makeOrderReadyActivate(orderId)
      }

      orderStore.clear()
      navigate(LKRoutesEnum.orders)
      viewOrderUiStore.setSelectedOrder(null)
    } catch (error) {
      notification.error({ message: (error as Error).message })
    }

    setIsModalOpen(false)
  }

  const getKey = (key: string) => `${OrderKeys.activateOrderPage}.${key}`

  return (
    <Flex vertical>
      <Form layout="vertical">
        {country === Country.BY && (
          <>
            <OrderBYCompanyForm />
            <Divider />
          </>
        )}

        <Margin mb={8}>
          <Title level={5}>{t(getKey('Вывеска'))}</Title>
        </Margin>

        <FormItem label={t(getKey('Размер вывески'))}>
          <ReactSelect
            value={uiStore.signboardSizeItem}
            onChange={(item) => uiStore.setSignboardSizeItem(item as BaseOption<SignboardSize>)}
            options={signboardSizeOptions}
            placeholder={t(getKey('Выберите размер вывески'))}
          />
        </FormItem>

        <FormItem label={t(getKey('Формат вывески'))}>
          <ReactSelect
            value={uiStore.signboardFormatItem}
            onChange={(item) => uiStore.setSignboardFormatItem(item as BaseOption<SignboardFormat>)}
            options={signboardFormatOptions.map((item) => ({ value: item.value, label: t(getKey(item.label)) }))}
            placeholder={t(getKey('Выберите формат вывески'))}
          />
        </FormItem>

        <Divider />
      </Form>

      <PickPointFilesForm uiStore={uiStore.fileList} isActivation initialized={uiStore.initialized} entity={order} />

      <Form layout="vertical">
        <FormItem label={t(getKey('Сообщение оператору'))}>
          <TextArea
            value={uiStore.comment}
            onChange={(e) => uiStore.setComment(e.target.value)}
            maxLength={500}
            placeholder={t(getKey('При необходимости опишите'))}
          />
        </FormItem>
      </Form>

      <Divider />

      <Title level={5}>{t(getKey('Активация вашего ПВЗ'))}</Title>

      <Info>
        <Flex vertical gap={8}>
          <Text>{t(getSharedKey('Обратите внимание'))}!</Text>
          <Text>{t(getKey('Активация подразумевает'))}</Text>
        </Flex>
      </Info>

      <Margin mt={24}>
        <Flex gap={8}>
          <Button onClick={() => uiStore.setSelectedStep(uiStore.selectedStep - 1)} type="default">
            {t(getCreateOrderPageKey('Предыдущий шаг'))}
          </Button>

          <Button
            onClick={validateForm}
            disabled={uiStore.isNoChanges && (eLockStatus === ElockInfoStatus.approved || !eLockStatus)}
          >
            {t(getKey('Активировать ПВЗ'))}
          </Button>
        </Flex>
      </Margin>

      <Modal
        title={t(getSharedKey('Внимание'))!}
        open={isModalOpen}
        onOk={activateOrderHandler}
        okButtonProps={{ loading }}
        okText={loading ? `${t(getSharedKey('Загрузка файлов'))}...` : t(getKey('Активировать ПВЗ'))}
        onCancel={() => setIsModalOpen(false)}
        cancelText={t(getSharedKey('cancel'))}
        centered
      >
        <Text>{t(getKey(country === Country.GE ? 'Ложная активация GE' : 'Ложная активация'))}</Text>{' '}
        <Text>{t(getKey('Подтверждение готовности принять товар'))}</Text>
      </Modal>
    </Flex>
  )
})
